import type { ReactElement, ReactNode } from 'react';
import { Alert } from 'antd';
import {
  Bar,
  BarChart as BaseBarChart,
  LabelList,
  Rectangle,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
  type BarShapeProps,
} from 'recharts';
import { calculateNiceTicks } from '../../../utils/chart';
import { TooltipContent } from './Tooltip';
import { formatCount, formatPercent } from '../../../utils/format';

type BarItem = {
  key: string;
  label: string;
  value: number;
};

type Props = {
  barWidth?: 'normal' | 'wide';
  disablePercentage?: boolean;
  data: BarItem[];
  noDataMessage: string;
  onBarItemClick?: (targetLabel: string) => void;
  selectedLabels?: string[];
  unit?: string;
};

const BAR_COLOR = '#023894';
const BAR_HEIGHT = {
  normal: 16,
  wide: 32,
} as const;
const BAR_GAP = {
  normal: 8,
  wide: 16,
} as const;
const X_LABEL_HEIGHT = 22 * 2;
const MAX_LABEL_LENGTH = 10;

const BarChart = ({
  data,
  noDataMessage,
  selectedLabels = [],
  unit = '',
  barWidth = 'normal',
  disablePercentage = false,
  onBarItemClick = undefined,
}: Props): ReactNode => {
  const sumValue = disablePercentage
    ? 0
    : data.reduce((sum: number, item: BarItem): number => {
        return sum + item.value;
      }, 0);

  const maxValue = Math.max(
    ...data.map((item: BarItem): number => {
      return item.value;
    })
  );

  return (
    <>
      {data.length > 0 && maxValue > 0 ? (
        <BaseBarChart
          data={data}
          layout="vertical"
          barSize={BAR_HEIGHT[barWidth]}
          barGap={BAR_GAP[barWidth]}
          responsive
          width="100%"
          height={
            data.length * (BAR_HEIGHT[barWidth] + BAR_GAP[barWidth]) +
            X_LABEL_HEIGHT
          }
          margin={{
            right: unit ? unit.length * 6 + 42 : disablePercentage ? 8 : 48,
          }}
        >
          <YAxis
            dataKey="label"
            type="category"
            interval={0}
            tickFormatter={(label: string): string => {
              return label.length > MAX_LABEL_LENGTH
                ? `${label.slice(0, MAX_LABEL_LENGTH - 1)}…`
                : label;
            }}
            fontSize={12}
            width="auto"
            cursor={onBarItemClick ? 'pointer' : 'default'}
            onClick={
              onBarItemClick
                ? ({ index }): void => {
                    if (data[index]?.key) {
                      onBarItemClick(data[index].key);
                    }
                  }
                : undefined
            }
          />
          <XAxis
            type="number"
            ticks={calculateNiceTicks('count', 0, maxValue)}
            label={
              unit
                ? {
                    value: `[${unit}]`,
                    position: 'right',
                    dx: unit.length * 2 + 10,
                    dy: -4,
                    fontSize: 12,
                  }
                : undefined
            }
            tickFormatter={(value: number): string => {
              return formatCount(value);
            }}
            fontSize={12}
          />
          <Tooltip
            content={({ active, payload }: TooltipContentProps): ReactNode => {
              if (active && payload && payload.length > 0) {
                const data: BarItem = payload[0].payload;
                return (
                  <TooltipContent
                    label={data.label}
                    values={[
                      {
                        fill: BAR_COLOR,
                        value: `${formatCount(data.value)}${
                          unit ? ` ${unit}` : ''
                        }`,
                      },
                    ]}
                  />
                );
              }
            }}
          />
          <Bar
            dataKey="value"
            fill={BAR_COLOR}
            radius={BAR_HEIGHT[barWidth] * 0.25}
            cursor={onBarItemClick ? 'pointer' : 'default'}
            shape={(props: BarShapeProps): ReactElement => {
              const item: BarItem | undefined = props.payload;

              return (
                <Rectangle
                  {...props}
                  fillOpacity={
                    selectedLabels.length === 0 ||
                    (item && selectedLabels.includes(item.key))
                      ? 1
                      : 0.4
                  }
                  height={BAR_HEIGHT[barWidth]}
                />
              );
            }}
            onClick={
              onBarItemClick
                ? (item: { payload?: { key?: string } }): void => {
                    if (item.payload?.key) {
                      onBarItemClick(item.payload.key);
                    }
                  }
                : undefined
            }
          >
            {!disablePercentage && (
              <LabelList
                dataKey="value"
                position="right"
                fontSize={12}
                formatter={(value: unknown): string => {
                  return typeof value === 'number' && sumValue > 0
                    ? formatPercent((value / sumValue) * 100)
                    : '';
                }}
              />
            )}
          </Bar>
        </BaseBarChart>
      ) : (
        <Alert
          type="warning"
          showIcon
          message={noDataMessage}
          style={{ borderRadius: '8px', marginBottom: '16px' }}
        />
      )}
    </>
  );
};

export type { BarItem };
export { BarChart };
